import { Button, Dropdown, Tag, Tooltip, message } from "antd";
import {
  EllipsisOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EquationParser } from "../../utils/equationParser";
import { validateEquationContent } from "../../utils/equationValidation";
import { ALL_OPERATOR_GUIDE_ENTRIES } from "../utils/operatorGuideData";
import { formatEquation } from "../utils/equationFormatter";
import {
  findEquationErrorRanges,
  IEquationErrorRange,
} from "../utils/equationErrorRanges";
import { IKnownVariableNames } from "../utils/variableHighlightPlugin";
import OperatorGuideModal from "./OperatorGuideModal";
import DryRunModal from "./DryRunModal";
import EquationCodeMirrorEditor from "./EquationCodeMirrorEditor";
import {
  IQuestionResponseVariable,
  IQuestionVariable,
  IRankOption,
  makeGenericSource,
  makeOperatorSource,
  makeQuestionVariableSource,
  makeRankSource,
  makeResponseVariableSource,
} from "./equationCompletions";
import { IPQuestion } from "modules/organization/models/interface";
import {
  RankingToolState,
  useRankingToolStore,
} from "modules/organization/store";
import { useShallow } from "zustand/react/shallow";

const RankingToolsEditor: FC<{
  questions?: IPQuestion[];
  projectSlug?: string;
  rankingProjectId?: number;
  rankingTypeId?: number | null;
}> = ({ questions, projectSlug, rankingProjectId, rankingTypeId }) => {
  const isPointBased = rankingTypeId === 2;
  const ranks = useRankingToolStore(
    useShallow((state: RankingToolState) => state.ranks),
  );
  const currentRankTab = useRankingToolStore(
    (state: RankingToolState) => state.currentRankTab,
  );
  const currentEquation = useRankingToolStore(
    useShallow((state: RankingToolState) => state.currentEquation),
  );
  const allEquations = useRankingToolStore(
    useShallow((state: RankingToolState) => state.allEquations),
  );
  const setCurrentTab = useRankingToolStore(
    (state: RankingToolState) => state.setCurrentTab,
  );
  const setEquationText = useRankingToolStore(
    (state: RankingToolState) => state.setEquationText,
  );
  const setScrollToVariable = useRankingToolStore(
    (state: RankingToolState) => state.setScrollToVariable,
  );
  const setActiveVariables = useRankingToolStore(
    (state: RankingToolState) => state.setActiveVariables,
  );
  const isSavingQuestionSnapshot = useRankingToolStore(
    (state: RankingToolState) => state.isSavingQuestionSnapshot,
  );
  const isQuestionSnapshotDirty = useRankingToolStore(
    (state: RankingToolState) => state.isQuestionSnapshotDirty,
  );
  const savedEquations = useRankingToolStore(
    useShallow((state: RankingToolState) => state.savedEquations),
  );
  const savingEquationRanks = useRankingToolStore(
    useShallow((state: RankingToolState) => state.savingEquationRanks),
  );
  // Scoped to the tab currently being viewed — a save in flight for a DIFFERENT rank (started,
  // then the user switched tabs before it resolved) must not show as loading/disabled here too.
  // A Set (not a single value) so two overlapping saves for different ranks don't clobber each
  // other's indicator.
  const isSavingCurrentEquation = savingEquationRanks.has(currentRankTab);
  const saveQuestionSnapshot = useRankingToolStore(
    (state: RankingToolState) => state.saveQuestionSnapshot,
  );
  const saveRankingEquation = useRankingToolStore(
    (state: RankingToolState) => state.saveRankingEquation,
  );
  const clearEquation = useRankingToolStore(
    (state: RankingToolState) => state.clearEquation,
  );
  const clearResponseScores = useRankingToolStore(
    (state: RankingToolState) => state.clearResponseScores,
  );
  const isCurrentEquationDirty =
    (currentEquation?.equation ?? "") !==
    (savedEquations[currentRankTab] ?? "");

  // Read by the CodeMirror completion sources (equationCompletions.ts) via getter closures —
  // kept as refs rather than React state since nothing here needs to re-render off them, and it
  // lets the sources array below stay referentially stable across renders.
  const questionVariableListRef = useRef<IQuestionVariable[]>([]);
  const questionResponseVariableListRef = useRef<IQuestionResponseVariable[]>(
    [],
  );
  const rankOptionsRef = useRef<IRankOption[]>([]);
  // A fresh object each time `questions` changes (never mutated in place) — passed to
  // EquationCodeMirrorEditor as a prop so its own effect can detect the change and push the new
  // sets into the editor's decoration state (variableHighlightPlugin.ts) via a dispatched
  // effect, without tearing down the EditorView.
  const [knownVariables, setKnownVariables] = useState<IKnownVariableNames>({
    question: new Set(),
    response: new Set(),
  });

  useEffect(() => {
    if (questions) {
      const _buildQuestionVariableList: IQuestionVariable[] = [];
      const _buildQuestionResponseVariableList: IQuestionResponseVariable[] =
        [];

      questions.forEach((eachQuestion) => {
        _buildQuestionVariableList.push({
          id: eachQuestion.id,
          variable: eachQuestion.question_variable,
          question: eachQuestion.question,
          question_order_text: eachQuestion.question_order_text,
        });

        eachQuestion.responses.forEach((eachQResponse) => {
          _buildQuestionResponseVariableList.push({
            id: eachQResponse.id,
            variable: eachQResponse.response_variable,
            question: eachQuestion.question,
            response: eachQResponse.response,
          });
        });
      });

      questionVariableListRef.current = _buildQuestionVariableList;
      questionResponseVariableListRef.current =
        _buildQuestionResponseVariableList;
      setKnownVariables({
        question: new Set(
          _buildQuestionVariableList.map((item) => item.variable),
        ),
        response: new Set(
          _buildQuestionResponseVariableList.map((item) => item.variable),
        ),
      });
    }
  }, [questions]);

  // This Ranking Project's other ranks, eligible for IMPORT(<rank>) — excludes the currently
  // selected tab (importing yourself isn't meaningful) AND any rank with a smaller display_order
  // than the current one — i.e. a higher rank — since that IMPORT would fail the tier-order check
  // anyway (a lower rank can only import an equal-or-lower rank, never a higher one). The typed-
  // substring filter itself lives in makeRankSource (equationCompletions.ts); this only computes
  // the eligible set.
  useEffect(() => {
    const selfDisplayOrder = currentEquation?.display_order;
    rankOptionsRef.current = ranks
      .filter((rank) => {
        if (rank === currentRankTab) {
          return false;
        }
        if (selfDisplayOrder != null) {
          const targetDisplayOrder = allEquations[rank]?.display_order;
          if (
            targetDisplayOrder != null &&
            targetDisplayOrder < selfDisplayOrder
          ) {
            return false;
          }
        }
        return true;
      })
      .map((rank) => ({ name: rank }));
  }, [ranks, currentRankTab, allEquations, currentEquation?.display_order]);

  // Stable across renders — each source reads live data through the refs above, so the
  // EditorView (built once from this array) never needs to be torn down and rebuilt.
  const completionSources = useMemo(
    () => [
      makeQuestionVariableSource(
        () => questionVariableListRef.current,
        setScrollToVariable,
      ),
      makeResponseVariableSource(
        () => questionResponseVariableListRef.current,
        setScrollToVariable,
      ),
      makeOperatorSource(ALL_OPERATOR_GUIDE_ENTRIES),
      makeRankSource(() => rankOptionsRef.current),
      makeGenericSource(
        () => questionVariableListRef.current,
        () => questionResponseVariableListRef.current,
        () => rankOptionsRef.current,
        ALL_OPERATOR_GUIDE_ENTRIES,
        setScrollToVariable,
      ),
    ],
    [setScrollToVariable],
  );

  const [equationError, setEquationError] = useState(0);
  // Every specific, pointed-out reason the current equation is invalid — the exact syntax
  // condition that failed (e.g. "Operator 'AND' requires at least one parameter.") when there's
  // a syntax error, otherwise every content problem found (unknown variable, unknown IMPORT
  // target, IMPORT tier-order violation) — never a generic "Equation has syntax error" label.
  const [equationErrors, setEquationErrors] = useState<string[]>([]);
  // Character ranges to underline in the editor (the wavy squiggle) — a presentation-only
  // companion to equationErrors above, computed from the same inputs but via a separate,
  // independent walk (equationErrorRanges.ts) so this can never change what equationErrors
  // itself contains or how Save/the error list behave.
  const [equationErrorRanges, setEquationErrorRanges] = useState<
    IEquationErrorRange[]
  >([]);
  const [isOperatorGuideOpen, setIsOperatorGuideOpen] = useState(false);
  const [isDryRunOpen, setIsDryRunOpen] = useState(false);
  // Collapsible so the error list doesn't permanently eat into the editor's vertical space —
  // starts collapsed (there's nothing useful to show yet), then the validation effect below
  // decides the real default the first time it evaluates each rank: OPEN only if that rank
  // actually HAS an error AND the viewport is tall enough to spare the room (existing
  // short-screen behavior preserved) — a clean equation never auto-opens the panel. Decided at
  // most once EVER per rank (autoOpenDecidedRanksRef, keyed by rank name — not just "the last
  // rank visited"), so switching away to another rank and back doesn't re-trigger the auto
  // decision and undo a manual open/close.
  const [isErrorAreaOpen, setIsErrorAreaOpen] = useState(false);
  const autoOpenDecidedRanksRef = useRef<Set<string>>(new Set());
  // Committed height, applied via inline style — only updated on drag END (see
  // handleErrorAreaResizeUp) or a +/- click, not on every mousemove, so dragging doesn't cause a
  // React re-render per pixel of mouse movement. During an active drag, the height is written
  // straight to the DOM (errorAreaRef) for a smooth 1:1 follow of the cursor.
  const ERROR_AREA_MIN_HEIGHT = 48;
  const ERROR_AREA_STEP = 40;
  const ERROR_AREA_TOP_GAP = 20;
  const rqlEditorPaneRef = useRef<HTMLDivElement>(null);
  const clampErrorAreaHeight = (height: number) => {
    const paneHeight = rqlEditorPaneRef.current?.getBoundingClientRect().height;
    const maxHeight =
      paneHeight != null
        ? paneHeight - ERROR_AREA_TOP_GAP
        : window.innerHeight * 0.6;
    return Math.min(maxHeight, Math.max(ERROR_AREA_MIN_HEIGHT, height));
  };
  const [errorAreaHeight, setErrorAreaHeight] = useState(128);
  const errorAreaRef = useRef<HTMLDivElement>(null);
  const errorAreaDragRef = useRef<{ startY: number; startHeight: number } | null>(
    null,
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const drag = errorAreaDragRef.current;
      if (!drag || !errorAreaRef.current) {
        return;
      }
      // Dragging the handle DOWN should make the error area SHORTER (its top edge moves down),
      // not taller — the panel's bottom edge is pinned by the layout below it (Prettify/Save
      // buttons), so growth has to come from the top, meaning cursor-down == height-down here.
      const delta = drag.startY - e.clientY;
      errorAreaRef.current.style.height = `${clampErrorAreaHeight(drag.startHeight + delta)}px`;
    };
    const handleMouseUp = () => {
      if (!errorAreaDragRef.current) {
        return;
      }
      errorAreaDragRef.current = null;
      if (errorAreaRef.current) {
        setErrorAreaHeight(errorAreaRef.current.getBoundingClientRect().height);
      }
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleErrorAreaResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    errorAreaDragRef.current = {
      startY: e.clientY,
      startHeight:
        errorAreaRef.current?.getBoundingClientRect().height ??
        errorAreaHeight,
    };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
  };

  // Keyboard/mouse-click alternative to dragging the resize handle — the handle itself has no
  // accessible (non-pointer) way to resize, so these +/- buttons are the only way a keyboard-only
  // user can adjust the error area's height at all.
  const handleErrorAreaGrow = () => {
    setErrorAreaHeight((prev) => clampErrorAreaHeight(prev + ERROR_AREA_STEP));
  };
  const handleErrorAreaShrink = () => {
    setErrorAreaHeight((prev) => clampErrorAreaHeight(prev - ERROR_AREA_STEP));
  };

  const handleChangeEquation = (data: string) => {
    setEquationText(data);
  };

  useEffect(() => {
    const equationText = currentEquation?.equation ?? "";
    const hasSyntaxError = new EquationParser(equationText).getHasError();
    setEquationError(hasSyntaxError);

    const rankDisplayOrderByName = new Map(
      Object.values(allEquations).map((eq) => [eq.name, eq.display_order]),
    );
    const contentErrors = validateEquationContent(
      equationText,
      questions ?? [],
      rankDisplayOrderByName,
      currentEquation?.display_order,
    );
    setEquationErrors(contentErrors);
    setEquationErrorRanges(
      findEquationErrorRanges(
        equationText,
        questions ?? [],
        rankDisplayOrderByName,
        currentEquation?.display_order,
      ),
    );

    if (currentRankTab && !autoOpenDecidedRanksRef.current.has(currentRankTab)) {
      autoOpenDecidedRanksRef.current.add(currentRankTab);
      const hasAnyError = hasSyntaxError > 0 || contentErrors.length > 0;
      const isViewportTallEnough =
        typeof window === "undefined" ||
        window.matchMedia("(min-height: 800px)").matches;
      setIsErrorAreaOpen(hasAnyError && isViewportTallEnough);
    }
  }, [
    currentEquation?.equation,
    currentEquation?.display_order,
    currentRankTab,
    questions,
    allEquations,
  ]);

  const handleSave = async () => {
    if (!projectSlug || !rankingProjectId) {
      return;
    }
    try {
      await saveQuestionSnapshot(projectSlug, rankingProjectId);
      message.success({
        key: "saveQuestionSnapshot",
        content: "Question snapshot saved successfully.",
        duration: 2,
      });
    } catch (error) {
      console.error(error);
      message.error({
        key: "saveQuestionSnapshot",
        content: "Failed to save question snapshot.",
      });
    }
  };

  const handleClear = () => {
    if (isPointBased) {
      clearResponseScores();
    }
  };

  const handleSaveEquation = async () => {
    if (!projectSlug || !rankingProjectId) {
      return;
    }
    if (equationError > 0 || equationErrors.length > 0) {
      return;
    }
    try {
      await saveRankingEquation(projectSlug, rankingProjectId);
      message.success({
        key: "saveEquation",
        content: `${currentRankTab} equation saved successfully.`,
        duration: 2,
      });
    } catch (error: any) {
      console.error(error);
      // error?.error?.response?.data?.message: a real API failure (see ApiService's
      // onResponseError). error.message on a plain Error: the store's own defensive
      // validateEquationContent re-check (should be unreachable since Save is already disabled
      // whenever equationErrors is non-empty, but keeps the real reason visible if it ever fires).
      const backendMessage = error?.error?.response?.data?.message;
      const contentMessage =
        !backendMessage && error instanceof Error ? error.message : null;
      message.error({
        key: "saveEquation",
        content:
          backendMessage ||
          contentMessage ||
          `Failed to save ${currentRankTab} equation.`,
      });
    }
  };

  const handleClearEquation = () => {
    clearEquation();
  };

  const handleFormatEquation = () => {
    const equationText = currentEquation?.equation ?? "";
    setEquationText(formatEquation(equationText));
  };

  const handlePrettifyShortcut = () => {
    if (!currentEquation?.equation || equationError > 0) {
      return;
    }
    handleFormatEquation();
  };

  const isRankEquationDirty = (rank: string) =>
    (allEquations[rank]?.equation ?? "") !== (savedEquations[rank] ?? "");

  const renderUnsavedTag = () => (
    <Tag
      style={{
        background: "transparent",
        border: "1px solid #972B38",
        color: "#972B38",
      }}
    >
      Unsaved
    </Tag>
  );

  // How many rank tabs fit on one row (each tab is flex:1 — see .tabMenu button in custom.css —
  // so beyond a certain count the strip grows past the page instead of wrapping/scrolling)
  // depends on the container's actual width AND each tab's own natural (unstretched) width —
  // both vary with viewport size, sidebar state, and the rank names themselves, so this can't be
  // a fixed number. Instead: render every tab a second time, invisibly and at natural width, in
  // a hidden row purely to measure it (measureRefs/moreBtnMeasureRef below), then compute how
  // many fit into the VISIBLE row's real width (tabMenuRef) and keep that in state, recomputed on
  // resize.
  const [visibleTabCount, setVisibleTabCount] = useState(ranks.length);
  const tabMenuRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef<Map<string, HTMLElement>>(new Map());
  const moreBtnMeasureRef = useRef<HTMLElement>(null);

  const recomputeVisibleTabCount = useCallback(() => {
    const container = tabMenuRef.current;
    if (!container || ranks.length === 0) {
      return;
    }
    // A small safety margin — offsetWidth rounds to whole pixels, and that rounding lands
    // differently at fractional browser zoom levels (110%, 90%, ...), so a sum of measured tab
    // widths that looks exactly equal to (or a hair under) the container's width can still
    // actually overflow once the browser's real (sub-pixel) layout runs. min-width:0 on the tab
    // chain (custom.css) means a miscount here would only clip locally, never blow out the page,
    // but leaving this margin avoids that clipping in the first place.
    const ZOOM_ROUNDING_BUFFER = 8;
    const availableWidth = container.clientWidth - ZOOM_ROUNDING_BUFFER;
    const tabWidths = ranks.map(
      (rank) => measureRefs.current.get(rank)?.offsetWidth ?? 0,
    );
    const totalTabWidth = tabWidths.reduce((sum, width) => sum + width, 0);
    if (totalTabWidth <= availableWidth) {
      setVisibleTabCount(ranks.length);
      return;
    }
    // Won't all fit — reserve room for the "more" trigger, then greedily fit as many tabs as
    // possible into what's left.
    const moreBtnWidth = moreBtnMeasureRef.current?.offsetWidth ?? 0;
    const availableForTabs = availableWidth - moreBtnWidth;
    let usedWidth = 0;
    let count = 0;
    for (const width of tabWidths) {
      if (usedWidth + width > availableForTabs) {
        break;
      }
      usedWidth += width;
      count++;
    }
    setVisibleTabCount(Math.max(1, count));
  }, [ranks]);

  // useLayoutEffect (not useEffect) — runs before paint, so an out-of-date visibleTabCount from a
  // previous set of ranks/container size never flashes on screen.
  useLayoutEffect(() => {
    recomputeVisibleTabCount();
  }, [recomputeVisibleTabCount]);

  useEffect(() => {
    const container = tabMenuRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => recomputeVisibleTabCount());
    observer.observe(container);
    return () => observer.disconnect();
  }, [recomputeVisibleTabCount]);

  const hasRankOverflow = visibleTabCount < ranks.length;
  let visibleRanks = ranks;
  let overflowRanks: string[] = [];
  if (hasRankOverflow) {
    const isCurrentTabWithinWindow =
      ranks.indexOf(currentRankTab) > -1 &&
      ranks.indexOf(currentRankTab) < visibleTabCount;
    visibleRanks = isCurrentTabWithinWindow
      ? ranks.slice(0, visibleTabCount)
      : [...ranks.slice(0, Math.max(visibleTabCount - 1, 0)), currentRankTab];
    overflowRanks = ranks.filter((rank) => !visibleRanks.includes(rank));
  }
  const isActiveTabInOverflow = overflowRanks.includes(currentRankTab);

  return (
    <div className="tabContainer">
      {!isPointBased && (
        <div
          className="tabMenu"
          role="tablist"
          aria-labelledby="tab-header"
          ref={tabMenuRef}
        >
          {/* Invisible, natural-width copy of every tab (plus the "more" trigger) purely for
              recomputeVisibleTabCount to measure via offsetWidth — never shown, never
              interactive. Kept in the DOM (not conditionally rendered) so ResizeObserver/layout
              effects always have something current to measure against. */}
          <div className="tabMenuMeasureRow" aria-hidden="true">
            {ranks.map((rank) => (
              <Button
                key={rank}
                tabIndex={-1}
                ref={(el) => {
                  if (el) {
                    measureRefs.current.set(rank, el);
                  } else {
                    measureRefs.current.delete(rank);
                  }
                }}
              >
                <span className="rankTabContent">
                  <span className="ellipsisTxt rankTabLabel" title={rank}>
                    {rank}
                  </span>
                  {isRankEquationDirty(rank) && renderUnsavedTag()}
                </span>
              </Button>
            ))}
            <Button
              tabIndex={-1}
              className="rankTabMoreBtn"
              ref={moreBtnMeasureRef}
            >
              <EllipsisOutlined role="img" aria-hidden />
            </Button>
          </div>
          {visibleRanks.map((rank) => (
            <Button
              onClick={() => setCurrentTab(rank)}
              key={rank}
              className={rank === currentRankTab ? "active" : ""}
            >
              <span className="rankTabContent">
                <span className="ellipsisTxt rankTabLabel" title={rank}>
                  {rank}
                </span>
                {isRankEquationDirty(rank) && renderUnsavedTag()}
              </span>
            </Button>
          ))}
          {hasRankOverflow && (
            <Dropdown
              trigger={["click"]}
              menu={{
                selectedKeys: isActiveTabInOverflow ? [currentRankTab] : [],
                onClick: ({ key }) => setCurrentTab(key),
                items: overflowRanks.map((rank) => ({
                  key: rank,
                  label: (
                    <span className="rankOverflowMenuItem">
                      <span className="ellipsisTxt" title={rank}>
                        {rank}
                      </span>
                      {isRankEquationDirty(rank) && renderUnsavedTag()}
                    </span>
                  ),
                })),
              }}
            >
              <Button
                className={
                  isActiveTabInOverflow ? "active rankTabMoreBtn" : "rankTabMoreBtn"
                }
                aria-label="More ranks"
              >
                <EllipsisOutlined role="img" aria-hidden />
              </Button>
            </Dropdown>
          )}
        </div>
      )}
      <div
        className={`tabContent${isPointBased ? " tabContentPointBased" : ""}`}
      >
        {!isPointBased && (
          <>
            <h3 className="rqlEditorHeading">
              RQL Editor
              <Tooltip
                placement="right"
                overlayClassName="rqlEditorInfoTooltip"
                trigger={["hover", "focus"]}
                overlayStyle={{ maxWidth: 500 }}
                title={
                  <ul className="rqlEditorInfoTooltipList">
                    <li>Tab inserts a tab character at the cursor instead of leaving the editor.</li>
                    <li>Escape moves focus out of the editor, forward to the next field.</li>
                    <li>Shift + Escape moves focus out of the editor, backward to the previous field.</li>
                    <li>Cmd/Ctrl + Shift + P runs Prettify without leaving the editor.</li>
                  </ul>
                }
              >
                <InfoCircleOutlined
                  className="rqlEditorHeadingInfo"
                  tabIndex={0}
                  role="img"
                  aria-label="Keyboard shortcuts for this editor"
                />
              </Tooltip>
            </h3>
            {/* The editor's own size (rqlEditorPane -> .rankingCodeMirror) is now completely
                stable — it no longer shrinks to make room for the error panel. Instead the error
                panel is an absolutely-positioned overlay (isErrorAreaOpen) that slides up and
                covers the BOTTOM portion of the editor when open, sized by dragging its handle.
                This sidesteps CodeMirror 6's own internal height/virtualization quirks entirely —
                its container's size is never touched by opening/closing/resizing the error panel. */}
            <div className="rqlEditorPane" ref={rqlEditorPaneRef}>
              <EquationCodeMirrorEditor
                value={currentEquation?.equation ?? ""}
                stateKey={currentRankTab}
                onChange={handleChangeEquation}
                hasError={equationError > 0}
                completionSources={completionSources}
                knownVariables={knownVariables}
                onActiveVariablesChange={setActiveVariables}
                onCursorEnterVariable={setScrollToVariable}
                errorRanges={equationErrorRanges}
                onPrettifyShortcut={handlePrettifyShortcut}
              />
              {isErrorAreaOpen && (
                <div
                  className="equationErrorOverlay"
                  ref={errorAreaRef}
                  style={{ height: clampErrorAreaHeight(errorAreaHeight) }}
                >
                  <div
                    className="equationErrorResizeHandle"
                    onMouseDown={handleErrorAreaResizeStart}
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label="Resize error messages area"
                  />
                  <div className="equationErrorArea">
                    {equationErrors.length > 0 && (
                      <ul>
                        {equationErrors.map((eachError, index) => (
                          <li key={index}>{eachError}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Deliberately a sibling of .rqlEditorPane, not nested inside it — this bar must
                stay fully clickable/visible at all times (including when the overlay above is
                closed), so it can never sit on top of the editor and block interacting with its
                bottom-most visible line. */}
            <div className="equationErrorToggleBar">
              <strong>
                Error messages
                {equationErrors.length > 0 && ` (${equationErrors.length})`}
              </strong>
              <div className="equationErrorBarActions">
                {/* The drag handle (above, on the overlay) has no non-pointer equivalent — these
                    are the only way a keyboard-only user can resize the error area at all, tabbed
                    to and activated with Enter/Space like any other button. Disabled while
                    collapsed, since there's nothing to resize. */}
                <Button
                  type="text"
                  size="small"
                  className="iconOnlyBtn smallIcon"
                  icon={<MinusOutlined role="img" aria-hidden />}
                  aria-label="Decrease error messages area size"
                  disabled={!isErrorAreaOpen}
                  onClick={handleErrorAreaShrink}
                />
                <Button
                  type="text"
                  size="small"
                  className="iconOnlyBtn smallIcon"
                  icon={<PlusOutlined role="img" aria-hidden />}
                  aria-label="Increase error messages area size"
                  disabled={!isErrorAreaOpen}
                  onClick={handleErrorAreaGrow}
                />
                <Button
                  type="text"
                  size="small"
                  className="equationErrorExpandBtn"
                  onClick={() => setIsErrorAreaOpen((prev) => !prev)}
                  aria-expanded={isErrorAreaOpen}
                >
                  {isErrorAreaOpen ? "Collapse" : "Expand"}
                </Button>
              </div>
            </div>
          </>
        )}

        <div className="btnWrap">
          {!isPointBased && (
            <>
              <Button
                className="linkBtn2"
                style={{marginLeft: '5px'}}
                onClick={() => setIsOperatorGuideOpen(true)}
              >
                Operation Guide
              </Button>
              <Button
                onClick={handleFormatEquation}
                disabled={
                  !currentEquation?.equation || equationError > 0
                }
              >
                Prettify
              </Button>
              <Button
                onClick={() => setIsDryRunOpen(true)}
                disabled={
                  !currentEquation?.equation ||
                  equationError > 0 ||
                  equationErrors.length > 0
                }
              >
                Explain
              </Button>
            </>
          )}
          {isPointBased ? (
            <>
              <Button onClick={handleClear}>Clear</Button>
              <Button
                type="primary"
                loading={isSavingQuestionSnapshot}
                disabled={
                  !projectSlug || !rankingProjectId || !isQuestionSnapshotDirty
                }
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleClearEquation}
                disabled={!isCurrentEquationDirty}
              >
                Clear
              </Button>
              <Button
                type="primary"
                loading={isSavingCurrentEquation}
                disabled={
                  !projectSlug ||
                  !rankingProjectId ||
                  !isCurrentEquationDirty ||
                  equationError > 0 ||
                  equationErrors.length > 0
                }
                onClick={handleSaveEquation}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>
      <OperatorGuideModal
        open={isOperatorGuideOpen}
        onClose={() => setIsOperatorGuideOpen(false)}
      />
      <DryRunModal
        open={isDryRunOpen}
        onClose={() => setIsDryRunOpen(false)}
        projectSlug={projectSlug}
        rankingProjectId={rankingProjectId}
        currentRankTab={currentRankTab}
        liveEquationText={currentEquation?.equation ?? ""}
        ranks={ranks}
        allEquations={allEquations}
        savedEquations={savedEquations}
        questions={questions ?? []}
      />
    </div>
  );
};

export default RankingToolsEditor;
